import { useRecoilValue } from 'recoil';
import {
    brushOptionsState,
    deformOptionsState,
    lineDeformOptionsState,
    rectangleSelectModeState,
    smoothBrushOptionsState,
    type StrokeMode,
    type Tools,
} from '~/state/atoms/toolsAtoms';
import { formatMmAsCm } from '~/utils/units';

/** Joins the values a tool options summary reads, in the order they show. */
export const SUMMARY_SEPARATOR = ' / ';

const diameterText = (diameterMm: number) =>
    `${formatMmAsCm(diameterMm, 1)} cm`;
const strokeModeText = (mode: StrokeMode) => mode.toUpperCase();

/**
 * The sizes and modes in force for a contouring tool, on one line, such as
 * `1.5 cm / 2D`, with their units. Undefined while the tool has no such
 * value to read.
 */
export function useToolOptionsSummary(tool: Tools): string | undefined {
    const values = useToolValues(tool);
    return values.length > 0 ? values.join(SUMMARY_SEPARATOR) : undefined;
}

/** The sizes and modes of `tool`, in the order its options show them. */
function useToolValues(tool: Tools): string[] {
    const brush = useRecoilValue(brushOptionsState);
    const rectangleMode = useRecoilValue(rectangleSelectModeState);
    const lineDeform = useRecoilValue(lineDeformOptionsState);
    const deform = useRecoilValue(deformOptionsState);
    const smoothBrush = useRecoilValue(smoothBrushOptionsState);

    switch (tool) {
        case 'brush':
            return [
                diameterText(brush.diameter),
                strokeModeText(brush.strokeMode),
            ];
        case 'rectangle-select':
            return [rectangleMode];
        case 'lineDeform':
            return [`${lineDeform.spreadPercent} %`];
        case 'deform':
            return [diameterText(deform.diameter), strokeModeText(deform.mode)];
        case 'smoothBrush':
            return [
                diameterText(smoothBrush.diameter),
                strokeModeText(smoothBrush.mode),
            ];
        default:
            return [];
    }
}
